/**
 * The ledger's money side: what moves on the bank, card and cash accounts, and what a fixed asset
 * costs each year. Next to `postingFor` (a document), these make the journal complete:
 *
 *  - a **payment**: a statement row linked to an invoice clears the supplier or customer (3300 /
 *    2000) against the account it was paid from. A Barbeleg needs none - its own posting is
 *    already paid from the till or the card;
 *  - a **row booked without a receipt**: to an expense or revenue category (bank charges, SVS,
 *    interest), as a private withdrawal or contribution, to the Finanzamt, or as a transfer to
 *    another of the business's own accounts. Without an invoice there is no input tax (§ 12
 *    UStG), so these carry no VAT;
 *  - the **opening balance** of an account, against the Eröffnungsbilanzkonto. Rows dated before
 *    it are already in it and post nothing;
 *  - the **AfA** of an asset, one entry per year (`lib/at/afa.ts`);
 *  - a **business trip** from the Fahrtenbuch: Kilometergeld, Tagesgeld and Nächtigungsgeld the
 *    owner paid privately (`lib/at/travel.ts`), an expense against a private contribution.
 *
 * A row nobody has explained yet posts nothing: it is what the banking page still asks about.
 * Same constraints as posting.ts: this is bundled into PocketBase's hooks.
 */
import { afaSchedule, type AssetInput } from "@/lib/at/afa";
import { tripAmounts, type TripInput } from "@/lib/at/travel";
import { round2 } from "@/lib/at/vat";
import { PAYABLES, RECEIVABLES, type LedgerLine, type LineRole, type Posting } from "./posting";

/** Privatentnahmen und -einlagen. */
export const PRIVATE = "9600";
/** Eröffnungsbilanzkonto. */
export const OPENING = "9800";
/** Verrechnungskonto Finanzamt: VAT payments and refunds. */
export const TAX_OFFICE = "3540";
/** Planmäßige Abschreibung, and the book value of an asset given up. */
export const DEPRECIATION = "7000";
export const DISPOSAL = "7820";
/** Kilometergeld, Tagesgeld and Nächtigungsgeld from the Fahrtenbuch. */
export const TRAVEL = "7340";
/** Betriebs- und Geschäftsausstattung, when an asset names no account of its own. */
export const ASSET_ACCOUNT = "0600";
/** The accounts a bank account or a card books on when the accountant named none. */
export const BANK = "2800";
export const CARD = "2810";

export type MoneyAccountType = "bank" | "credit_card" | "cash";

/** The Sachkonto a bank account, card or till books on. */
export function moneyAccount(
  account: { type: MoneyAccountType; ledgerAccount?: string },
  cashAccount: string,
): string {
  const own = (account.ledgerAccount ?? "").trim();
  if (own) return own;
  if (account.type === "cash") return cashAccount;
  return account.type === "credit_card" ? CARD : BANK;
}

export type BookingType = "category" | "private" | "transfer" | "tax";

export type MoneyTransaction = {
  id: string;
  workspace: string;
  /** Euros; negative is money out. */
  amount: number;
  currency?: string;
  bookingDate: string;
  status: "unmatched" | "matched" | "ignored";
  bookingType?: BookingType | "";
  counterparty?: string;
  /** Set on the incoming half of a transfer whose outgoing half is booked too. */
  transferOf?: string;
};

export type TransactionContext = {
  /** The Sachkonto of the row's own account (`moneyAccount`). */
  account: string;
  /** Rows before the opening balance's date are already in it. */
  openingDate?: string;
  /** The linked document, when there is one. */
  document?: {
    kind: "ER" | "AR" | "KA";
    docNumber?: string;
    partnerName?: string;
    partnerVatId?: string;
  } | null;
  /** The category's account, for a row booked to a category. */
  categoryAccount?: string;
  /** The other account's Sachkonto, for a transfer. */
  transferAccount?: string;
};

export type MoneyProblem = "foreign_currency" | "no_account" | "no_transfer_account";

const cents = (euros: number) => Math.round(round2(euros) * 100);

function lineBook(lines: LedgerLine[]) {
  // A positive amount lands on `side`, a negative one on the other.
  return (account: string, amount: number, side: "debit" | "credit", role: LineRole) => {
    if (amount === 0) return;
    const debit = (side === "debit") === amount > 0;
    const value = Math.abs(amount);
    lines.push({ account, debit: debit ? value : 0, credit: debit ? 0 : value, role, vatRate: 0 });
  };
}

const empty: Omit<Posting, "date" | "lines"> = {
  kind: "",
  taxTreatment: "",
  docNumber: "",
  partnerName: "",
  partnerVatId: "",
};

/**
 * The entry a statement row should have: null while nothing explains it (or it is covered by the
 * opening balance), a problem when it is explained but cannot be posted.
 */
export function transactionPosting(
  transaction: MoneyTransaction,
  context: TransactionContext,
): Posting | { problem: MoneyProblem } | null {
  const date = transaction.bookingDate.slice(0, 10);
  if (context.openingDate && date < context.openingDate.slice(0, 10)) return null;

  const amount = cents(transaction.amount);
  const lines: LedgerLine[] = [];
  const book = lineBook(lines);
  const base = { ...empty, date, partnerName: transaction.counterparty ?? "" };

  if (transaction.status === "matched" && context.document) {
    const { kind } = context.document;
    // A Barbeleg's own posting is already paid from this account (`paidFrom`).
    if (kind === "KA") return null;
    if ((transaction.currency || "EUR") !== "EUR") return { problem: "foreign_currency" };
    book(context.account, amount, "debit", "money");
    book(kind === "AR" ? RECEIVABLES : PAYABLES, amount, "credit", "settlement");
    return {
      ...base,
      kind,
      docNumber: context.document.docNumber ?? "",
      partnerName: context.document.partnerName ?? base.partnerName,
      partnerVatId: (context.document.partnerVatId ?? "").replace(/\s+/g, "").toUpperCase(),
      lines,
    };
  }

  if (transaction.status !== "ignored" || !transaction.bookingType) return null;
  if ((transaction.currency || "EUR") !== "EUR") return { problem: "foreign_currency" };

  let other: string;
  let role: LineRole = "other";
  switch (transaction.bookingType) {
    case "category":
      other = (context.categoryAccount ?? "").trim();
      if (!other) return { problem: "no_account" };
      role = "base";
      break;
    case "private":
      other = PRIVATE;
      break;
    case "tax":
      other = TAX_OFFICE;
      break;
    case "transfer":
      // Both halves imported: the outgoing one carries the movement.
      if (transaction.transferOf && amount > 0) return null;
      other = (context.transferAccount ?? "").trim();
      if (!other) return { problem: "no_transfer_account" };
      break;
  }
  book(context.account, amount, "debit", "money");
  book(other, amount, "credit", role);
  return { ...base, lines };
}

export type OpeningAccount = {
  workspace: string;
  openingBalance?: number;
  openingDate?: string;
};

/** The opening balance of an account: on it, against the Eröffnungsbilanzkonto. */
export function openingPosting(account: OpeningAccount, ledgerAccount: string): Posting | null {
  const amount = cents(account.openingBalance ?? 0);
  const date = (account.openingDate ?? "").slice(0, 10);
  if (amount === 0 || !date) return null;
  const lines: LedgerLine[] = [];
  const book = lineBook(lines);
  book(ledgerAccount, amount, "debit", "money");
  book(OPENING, amount, "credit", "other");
  return { ...empty, date, lines };
}

export type LedgerAsset = AssetInput & {
  workspace: string;
  name: string;
  /** The asset account; empty for the default. */
  account?: string;
};

/** The AfA of one year as an entry: dated 31 December, or the day the asset was given up. */
export function depreciationPosting(asset: LedgerAsset, year: number): Posting | null {
  const row = afaSchedule(asset).find((entry) => entry.year === year);
  if (!row) return null;
  const share = 1 - Math.min(100, Math.max(0, asset.privateUsePercent ?? 0)) / 100;
  const business = Math.round(row.depreciation * share);
  const lines: LedgerLine[] = [];
  const book = lineBook(lines);
  book(DEPRECIATION, business, "debit", "base");
  book(DISPOSAL, row.deductible - business, "debit", "base");
  book(PRIVATE, row.depreciation + row.residual - row.deductible, "debit", "other");
  book(
    (asset.account ?? "").trim() || ASSET_ACCOUNT,
    row.depreciation + row.residual,
    "credit",
    "other",
  );
  const disposal = (asset.disposalDate ?? "").slice(0, 10);
  return {
    ...empty,
    date: disposal && disposal.startsWith(String(year)) ? disposal : `${year}-12-31`,
    partnerName: asset.name,
    lines,
  };
}

/** The years an asset has AfA in. */
export function depreciationYears(asset: AssetInput): number[] {
  return afaSchedule(asset).map((row) => row.year);
}

export type LedgerTrip = TripInput & {
  workspace: string;
  destination?: string;
  purpose?: string;
};

/**
 * A trip's entry, on the day it started: the expense against Privat, since the owner paid with
 * their own car and pocket. `kmBefore` is what the year's cap already used (`kmBefore` in travel.ts).
 */
export function tripPosting(trip: LedgerTrip, kmBefore: number): Posting | null {
  const { total } = tripAmounts(trip, kmBefore);
  if (total === 0) return null;
  const lines: LedgerLine[] = [];
  const book = lineBook(lines);
  book(TRAVEL, total, "debit", "base");
  book(PRIVATE, total, "credit", "other");
  return {
    ...empty,
    date: trip.date.slice(0, 10),
    partnerName: [trip.destination, trip.purpose].filter(Boolean).join(" · ").slice(0, 200),
    lines,
  };
}
